import { IssueState } from "@boark/shared/types/issue";
import { IssueLink, IssueLinkType } from "@boark/shared/types/issue-link";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { FileList } from "../../components/file-list";
import { H1 } from "../../components/h1";
import { IssueComments } from "../../components/issue-comments";
import { Modal } from "../../components/modal";
import { useGetAssociation } from "../../queries/get-association";
import { useGetAssociationContext } from "../../queries/get-association-context";
import { useGetIssue } from "../../queries/issue";
import { getId } from "../../utils/id";
import { useLocale } from "../../stores/locale";
import { formatDate } from "../../utils/date";
import { navigateBack } from "../../utils/navigate-back";

export function IssueView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { locale: t } = useLocale();

  if (!id) {
    throw new Error("Issue identifier is required");
  }

  const { data: issue } = useGetIssue(id);
  const { data: associationContext } = useGetAssociationContext();
  const { data: association } = useGetAssociation(
    getId(associationContext?.id ?? ""),
  );
  const hasFastumIntegration = Boolean(association?.integrations?.fastum);

  const closeModal = () => {
    navigateBack(navigate, "/admin/issue/list");
  };

  const stateLabel = (state: IssueState) => {
    switch (state) {
      case IssueState.TODO:
        return t.issue.stateTodo;
      case IssueState.IN_PROGRESS:
        return t.issue.stateInProgress;
      case IssueState.DONE:
        return t.issue.stateDone;
      case IssueState.WONTDO:
        return t.issue.stateWontdo;
    }
  };

  const uniqueLinks = useMemo(() => {
    if (!issue?.links.length) {
      return [];
    }
    const seen = new Set<string>();
    return issue.links.filter((link) => {
      if (seen.has(link.id)) {
        return false;
      }
      seen.add(link.id);
      return true;
    });
  }, [issue?.links]);

  const linkRowLabel = (link: IssueLink) => {
    const other = "in" in link ? link.in : link.out;
    const verb =
      link.type === IssueLinkType.RELATE
        ? t.issue.linkRelatedTo
        : "in" in link
          ? t.issue.linkBlockedBy
          : t.issue.linkBlocks;
    return `${verb} #${other.identifier} — ${other.title}`;
  };

  return (
    <Modal open onClose={closeModal}>
      {issue && (
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <H1>{issue.title}</H1>
            <Field label={t.issue.identifier}>#{issue.identifier}</Field>
            <Field label={t.issue.state}>{stateLabel(issue.state)}</Field>
            <Field label={t.issue.createdBy}>{issue.createdBy.email}</Field>
            <Field label={t.issue.createdAt}>
              {formatDate(issue.createdAt)}
            </Field>
            <Field label={t.issue.updatedAt}>
              {formatDate(issue.updatedAt)}
            </Field>
            <Field label={t.issue.description}>
              {issue.description?.trim()
                ? issue.description
                : t.issue.descriptionEmpty}
            </Field>
            <Field label={t.issue.tags}>
              {issue.tags.length > 0
                ? issue.tags.join(", ")
                : t.issue.descriptionEmpty}
            </Field>
            <Field label={t.issue.links}>
              {uniqueLinks.length === 0 ? (
                t.issue.linksEmpty
              ) : (
                <ul className="list-disc pl-5 m-0">
                  {uniqueLinks.map((link) => (
                    <li key={link.id}>{linkRowLabel(link)}</li>
                  ))}
                </ul>
              )}
            </Field>
            {hasFastumIntegration && (
              <Field label={t.issue.fastumTicketsLinked}>
                {issue.fastumTickets.length === 0 ? (
                  t.issue.fastumTicketsLinkedEmpty
                ) : (
                  <ul className="list-disc pl-5 m-0">
                    {issue.fastumTickets.map((ticket) => (
                      <li key={ticket.id}>
                        <a
                          className="text-blue-700 hover:underline"
                          href={ticket.link}
                          rel="noreferrer"
                          target="_blank"
                        >
                          {ticket.externalId} — {ticket.title} (
                          {ticket.status ?? t.issue.descriptionEmpty})
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </Field>
            )}
          </div>
          <IssueComments issueIdentifier={issue.identifier} />
          <FileList rootPath={`issue/${issue.identifier}`} />
        </div>
      )}
    </Modal>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-2">
      <span className="font-bold shrink-0">{label}:</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
